import * as XLSX from 'xlsx';
import {
  fetchConversationSessions,
  fetchMessages,
} from '../services/boardChatService';
import {
  assigneeFromConversation,
  contactNameFromConversation,
  lastMessagePreview,
  normalizeMessageType,
  sessionStatusLabel,
  teamNameFromConversation,
  translateActivityMessage,
} from './boardChatUtils';
import { parseAttachmentContent } from './boardChatAttachmentUtils';
import { periodLabel } from './boardChatResolvedFilterUtils';

const HEADERS = {
  fill: { fgColor: { rgb: 'FF7C4DFF' } },
  font: { bold: true, color: { rgb: 'FFFFFFFF' } },
  alignment: { vertical: 'center', wrapText: true },
};

function dateTime(value) {
  if (value == null || value === '') return '—';
  const normalized =
    typeof value === 'number' && value < 1e12 ? value * 1000 : value;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
}

function senderFromConversation(conv) {
  return conv?.meta?.sender || conv?.contact || {};
}

function messageLabel(message) {
  const type = normalizeMessageType(message);
  if (type === 'incoming') return 'Cliente';
  if (type === 'outgoing') return 'Atendente';
  if (type === 'private') return 'Nota privada';
  return 'Atividade';
}

function messageSender(message, conv) {
  const sender = message?.sender || {};
  return String(
    sender.name ||
      sender.available_name ||
      sender.email ||
      (normalizeMessageType(message) === 'incoming'
        ? contactNameFromConversation(conv)
        : 'Sistema')
  );
}

function messageContent(message) {
  const type = normalizeMessageType(message);
  if (type === 'activity') return translateActivityMessage(message?.content);
  const attachment = parseAttachmentContent(message?.content);
  if (!attachment) return String(message?.content || '').trim() || '—';
  return attachment.caption || `[Anexo] ${attachment.filename || 'Arquivo'}`;
}

function attachmentLink(message) {
  const attachment = parseAttachmentContent(message?.content);
  return attachment?.url || attachment?.downloadUrl || attachment?.key || '—';
}

function durationMinutes(start, end) {
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '—';
  return Math.max(0, Math.round((b - a) / 60000));
}

function filtersRows(filters) {
  const rows = [];
  if (filters.period?.startDate && filters.period?.endDate) {
    rows.push(['Período', periodLabel(filters.period)]);
  }
  if (String(filters.protocol || '').trim()) {
    rows.push(['Protocolo', String(filters.protocol).trim()]);
  }
  if (filters.userIds?.length) {
    rows.push(['Usuários (IDs)', filters.userIds.join(', ')]);
  }
  if (String(filters.text || '').trim()) {
    rows.push(['Texto', String(filters.text).trim()]);
  }
  if (filters.assigneeIds?.length) {
    rows.push(['Atendentes (IDs)', filters.assigneeIds.join(', ')]);
  }
  return rows;
}

function styleSheet(ws, headerRow, widths) {
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cell = ws[XLSX.utils.encode_cell({ r: headerRow, c })];
    if (cell) cell.s = HEADERS;
  }
  ws['!cols'] = widths.map((wch) => ({ wch }));
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRow, c: 0 },
      e: { r: range.e.r, c: range.e.c },
    }),
  };
  ws['!freeze'] = { xSplit: 0, ySplit: headerRow + 1 };
}

async function loadHistories(conversations) {
  const results = [];
  const chunkSize = 4;
  for (let i = 0; i < conversations.length; i += chunkSize) {
    const chunk = conversations.slice(i, i + chunkSize);
    const loaded = await Promise.all(
      chunk.map(async (conversation) => {
        const [messagesRes, sessionsRes] = await Promise.all([
          fetchMessages(conversation.id),
          fetchConversationSessions(conversation.id).catch(() => ({ data: [] })),
        ]);
        return {
          conversation,
          messages: Array.isArray(messagesRes?.data) ? messagesRes.data : [],
          sessions: Array.isArray(sessionsRes?.data) ? sessionsRes.data : [],
        };
      })
    );
    results.push(...loaded);
  }
  return results;
}

export async function exportResolvedConversationsXlsx({
  conversations,
  filters,
  teams = [],
}) {
  if (!conversations?.length) {
    throw new Error('Nenhuma conversa encontrada com os filtros aplicados.');
  }

  const histories = await loadHistories(conversations);
  const filterRows = filtersRows(filters);
  const conversationsHeaderRow = 6 + filterRows.length;
  const conversationsMatrix = [
    ['Relatório de conversas resolvidas'],
    ['Gerado em', dateTime(Date.now())],
    ['Conversas exportadas', conversations.length],
    [],
    ['Filtros aplicados'],
    ...filterRows,
    [],
    [
      'Protocolo',
      'Usuário',
      'E-mail',
      'Telefone',
      'Time',
      'Quem atendeu',
      'Criada em',
      'Última atividade',
      'Mensagens',
      'Atendimentos',
      'Última mensagem',
    ],
    ...histories.map(({ conversation, messages, sessions }) => {
      const sender = senderFromConversation(conversation);
      const assignee = assigneeFromConversation(conversation);
      return [
        conversation.id,
        contactNameFromConversation(conversation),
        sender.email || '—',
        sender.phone_number || sender.phone || '—',
        teamNameFromConversation(conversation, teams) || '—',
        assignee?.name || '—',
        dateTime(conversation.created_at),
        dateTime(conversation.last_activity_at || conversation.timestamp),
        messages.length,
        sessions.length,
        lastMessagePreview(conversation) || '—',
      ];
    }),
  ];

  const messageRows = histories.flatMap(({ conversation, messages }) =>
    messages.map((message, index) => [
      conversation.id,
      contactNameFromConversation(conversation),
      index + 1,
      dateTime(message.created_at),
      messageLabel(message),
      messageSender(message, conversation),
      messageContent(message),
      attachmentLink(message),
    ])
  );

  const sessionRows = histories.flatMap(({ conversation, sessions }) =>
    sessions.map((session, index) => [
      conversation.id,
      contactNameFromConversation(conversation),
      index + 1,
      sessionStatusLabel(session.status),
      session.teamId ?? '—',
      dateTime(session.startedAt),
      dateTime(session.endedAt),
      session.endedAt ? durationMinutes(session.startedAt, session.endedAt) : '—',
    ])
  );

  const wb = XLSX.utils.book_new();
  const conversationsWs = XLSX.utils.aoa_to_sheet(conversationsMatrix);
  const messagesWs = XLSX.utils.aoa_to_sheet([
    ['Protocolo', 'Usuário', 'Ordem', 'Data/hora', 'Tipo', 'Autor', 'Conteúdo', 'Anexo'],
    ...messageRows,
  ]);
  const sessionsWs = XLSX.utils.aoa_to_sheet([
    ['Protocolo', 'Usuário', 'Sessão', 'Status final', 'ID do time', 'Início', 'Fim', 'Duração (min)'],
    ...sessionRows,
  ]);

  if (conversationsWs.A1) {
    conversationsWs.A1.s = {
      font: { bold: true, sz: 15, color: { rgb: 'FF7C4DFF' } },
    };
  }
  styleSheet(
    conversationsWs,
    conversationsHeaderRow,
    [12, 24, 30, 18, 22, 22, 20, 20, 12, 14, 48]
  );
  styleSheet(messagesWs, 0, [12, 24, 8, 20, 14, 24, 70, 45]);
  styleSheet(sessionsWs, 0, [12, 24, 8, 16, 12, 20, 20, 16]);

  XLSX.utils.book_append_sheet(wb, conversationsWs, 'Conversas');
  XLSX.utils.book_append_sheet(wb, messagesWs, 'Mensagens');
  XLSX.utils.book_append_sheet(wb, sessionsWs, 'Atendimentos');

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `chat-conversas-resolvidas-${stamp}.xlsx`);
}
